import range from "lodash/range";
import {
	CABINS_CODE,
	DESTINATION_LEVELS,
	HOTELONLY_SEARCH_ADULT,
	HOTELONLY_SEARCH_CHILDREN,
	HOTELONLY_SEARCH_DURATION,
	HOTELONLY_SEARCH_OFFSET,
	PASSENGER_TYPE,
	SDP_ROTATION_DATE_FORMAT,
	TRANSPORTATION_TYPES,
} from "app/constants";
import { addDays, addHours, format, subHours } from "date-fns";
import { stringify } from "qs";
import { sendTagOnSDPSearch } from "app/utils/analytics";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import orderBy from "lodash/orderBy";
import { convertUTCTimestampToYYYYMMDD, getDuration } from "app/utils/utils";
import { getLatestSearchEntry } from "app/features/SearchEngine/LastSearch/hooks/useSearchHistory";

export const getTravelLocationDataFromQuery = ({
	queryParams = {},
	destinations = [],
	departures = [],
}) => {
	const departure =
		departures.find(city =>
			[city?.code, ...(city?.airportIATAs ?? [])]
				?.filter(Boolean)
				?.includes(queryParams?.departureCityCode)
		) || {};

	let destination = {};
	if (queryParams?.destinationIATA) {
		const destinationsMatched = destinations?.filter(destination =>
			[
				destination?.airportGroup,
				...(destination?.airportIATAs?.map(iata => iata?.name) ?? []),
			]
				?.filter(Boolean)
				?.includes(queryParams?.destinationIATA)
		);
		destination =
			destinationsMatched?.find(dest =>
				dest?.airportIATAs?.some(iata => iata?.isMain === true)
			) ||
			destinationsMatched?.[0] ||
			{};
	} else {
		destination = destinations.find(
			destination => String(destination.code) === String(queryParams?.destinationResortCode)
		);
	}
	return { departure, destination };
};

export const getLastSearchData = (state, websiteTunnelType) => {
	const entry = getLatestSearchEntry(state.shop, websiteTunnelType);
	return entry?.data;
};

export const mapOccupanciesParamsToPayload = (occupancies = []) => {
	return occupancies?.map(occupancy => {
		const childrenCount = Number(occupancy.children);

		if (childrenCount > 0) {
			let childrenBirthdates = [...Array(childrenCount)].map(() => ({
				day: undefined,
				month: undefined,
				year: undefined,
			}));

			// childrenBirthdate.day="" from query params if coming from emirates website
			// because they do not provide birthdates
			if (occupancy.childrenBirthdates) {
				childrenBirthdates = occupancy.childrenBirthdates.map(childrenBirthdate => {
					return {
						day:
							childrenBirthdate.day || childrenBirthdate.day === 0
								? Number(childrenBirthdate.day)
								: undefined,
						month:
							childrenBirthdate.month || childrenBirthdate.month === 0
								? Number(childrenBirthdate.month) + 1
								: undefined,
						year:
							childrenBirthdate.year || childrenBirthdate.year === 0
								? Number(childrenBirthdate.year)
								: undefined,
					};
				});
			}

			return {
				adults: Number(occupancy.adults),
				children: Number(occupancy.children),
				childrenBirthdates,
			};
		}
		return { adults: Number(occupancy.adults) };
	});
};

export const buildSDPQuotePayload = ({
	queryParams = {},
	partnerCode,
	destinations = [],
	sdpFunnelFlags,
	currency,
	duration,
}) => {
	const { destination } = getTravelLocationDataFromQuery({ queryParams, destinations });
	const requestBody = {
		departureCityCode: queryParams.departureCityCode,
		departureDate: Number(queryParams.departureDate),
		destinationResort: destination?.code,
		duration: Number(queryParams?.duration) || duration,
		cabin: queryParams.cabinCode,
		accommodationId: queryParams.accommodationId,
		partnerCode,
	};

	if (sdpFunnelFlags) {
		requestBody.abTests = sdpFunnelFlags;
	}

	requestBody.occupancies = mapOccupanciesParamsToPayload(queryParams.occupancies);
	requestBody.currency = currency;
	return requestBody;
};

export const buildStopoverQuotePayload = ({
	queryParams = {},
	marketingCode,
	destinations = [],
	sdpFunnelFlags,
}) => {
	const { destination } = getTravelLocationDataFromQuery({ queryParams, destinations });

	const requestBody = {
		origin: queryParams.departureCityCode,
		departureDate: Number(queryParams.departureDate),
		destinationId: destination?.code,
		duration: Number(queryParams.duration),
		cabin: CABINS_CODE[queryParams.cabinCode],
		stopOverConfig: {
			stopOverId: Number(queryParams.stopoverCityId),
			stopAt: Number(queryParams.stopoverDirection),
		},
		marketingCode,
	};
	if (sdpFunnelFlags) {
		requestBody.abTests = sdpFunnelFlags;
	}

	requestBody.occupancies = mapOccupanciesParamsToPayload(queryParams.occupancies);
	return requestBody;
};

export const getMinAndMaxPrice = rangeStr => {
	const ranges = Array.from(
		rangeStr.matchAll(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/g),
		m => [Number(m[1]), Number(m[2])]
	);

	if (ranges.length === 0) {
		return { min: 0, max: 0 };
	}

	const minPrices = ranges.map(([low]) => low);
	const maxPrices = ranges.map(([, high]) => high);

	const min = Math.min(...minPrices);
	const hasNoMaxPrice = maxPrices.some(price => price === 0);
	const max = hasNoMaxPrice ? 0 : Math.max(...maxPrices);

	return { min, max };
};

export const buildHotelOnlyQuotePayload = ({
	queryParams = {},
	marketingCode,
	destinations = [],
}) => {
	const { destination } = getTravelLocationDataFromQuery({ queryParams, destinations });
	const checkInDate = new Date(Number(queryParams.departureDate));
	const requestBody = {
		checkInDate: format(checkInDate, "yyyy-MM-dd"),
		marketingCode: marketingCode,
		destinationId: destination?.code,
		nights: Number(queryParams.duration),
		pagination: {
			page: queryParams.page,
			pageSize: queryParams.pageSize,
		},
	};

	if (queryParams.departureDate) {
		requestBody.checkInDate = convertUTCTimestampToYYYYMMDD(queryParams.departureDate);
	} else {
		throw new Error("checkInDate is required");
	}

	requestBody.marketingCode = marketingCode;

	requestBody.nights = Number(queryParams.duration) || 7;

	requestBody.pagination = {
		page: Number(queryParams.page) || 1,
		pageSize: Number(queryParams.pageSize) || 48,
	};

	if (queryParams.destinationResortCode) {
		requestBody.destinationId = Number(queryParams.destinationResortCode);
	} else {
		throw new Error("destinationId is required");
	}

	const filters = {};

	if (queryParams.tripadvisor) {
		filters.tripadvisor = Number(queryParams.tripadvisor);
	}

	if (queryParams.ratingStars) {
		filters.ratingStars = queryParams.ratingStars.split(",").map(Number);
	}

	if (queryParams.destination) {
		filters.destination = queryParams.destination.split(",").map(Number);
	}

	if (queryParams.propertyName) {
		filters.propertyName = queryParams.propertyName.split(",").map(Number);
	}

	if (queryParams.board) {
		filters.board = queryParams.board.split(",");
	}

	if (queryParams.cancellationType) {
		filters.cancellationType = queryParams.cancellationType;
	}

	if (queryParams.sort) {
		filters.sort = queryParams.sort;
	} else {
		filters.sort = "recommended";
	}

	if (queryParams.budgetMin || queryParams.budgetMax) {
		filters.budget = {
			min: Number(queryParams.budgetMin) || 0,
			max: Number(queryParams.budgetMax) || 0,
		};
	}

	if (queryParams.budget) {
		filters.budget = getMinAndMaxPrice(queryParams.budget);
	}

	requestBody.filters = Object.fromEntries(
		Object.entries(filters).filter(([value]) => value !== undefined && value !== null)
	);

	const newOccupancies =
		queryParams.occupancies &&
		queryParams.occupancies.map(occupancy => {
			if (occupancy.childrenBirthdates) {
				const childrenBirthDates = occupancy.childrenBirthdates.map(childrenBirthdate => {
					const year =
						childrenBirthdate.year || childrenBirthdate.year === 0
							? Number(childrenBirthdate.year)
							: undefined;
					const month =
						childrenBirthdate.month || childrenBirthdate.month === 0
							? String(Number(childrenBirthdate.month) + 1).padStart(2, "0")
							: undefined;
					const day =
						childrenBirthdate.day || childrenBirthdate.day === 0
							? String(Number(childrenBirthdate.day)).padStart(2, "0")
							: undefined;

					return year && month && day ? `${year}-${month}-${day}` : undefined;
				});

				return {
					adults: Number(occupancy.adults),
					childrenBirthDates: childrenBirthDates,
				};
			}
			return { adults: Number(occupancy.adults) };
		});

	requestBody.occupancies = newOccupancies;

	return requestBody;
};

export const getFlightNegotiatedLabelKey = (flight = {}) => {
	const isDirectFlight =
		flight?.outbound?.legs?.length === 1 && flight?.inbound?.legs?.length === 1;

	const isBaggageFree = flight?.flightOptions?.flightBagageOptions?.[0]?.pricePerBooking === 0;

	const isTravelByTrain = flight.type === TRANSPORTATION_TYPES.TRAIN;

	// Pour les transports (trains), garder l'ancienne logique
	if (isTravelByTrain) {
		const isLuggageIncluded = flight?.flightOptions?.flightBagageOption?.included;

		if (isDirectFlight && !isLuggageIncluded) {
			return "sdp.listing.product.direct.transport.negotiated.label";
		} else if (isLuggageIncluded) {
			return "sdp.listing.product.luggage.transport.negotiated.label";
		}
		return "sdp.listing.product.transport.negotiated.label";
	}

	// Nouvelle logique pour les vols
	const isCabinBaggageIncluded =
		flight?.flightOptions?.flightBagageOption?.cabinBaggage?.included;
	const isLuggageIncluded = flight?.flightOptions?.flightBagageOption?.included;
	const hasBaggage = isCabinBaggageIncluded || isLuggageIncluded || isBaggageFree;

	if (isDirectFlight && !hasBaggage) {
		// Vol direct inclus
		return "flight.baggage.block.direct.flight.included";
	} else if (isDirectFlight && hasBaggage) {
		// Vol direct et bagages inclus
		return "flight.baggage.block.direct.flight.baggage.included";
	} else if (!isDirectFlight && hasBaggage) {
		// Vol et bagages inclus
		return "flight.baggage.block.flight.baggage.included";
	}
	// Vol inclus : valeur par défaut
	return "flight.baggage.block.flight.included";
};

export function buildSDPBudgetFilterValues(accommodationsList, basePrice, travellersTotalCount) {
	const allPrices = accommodationsList
		.map(accommodation => {
			return getAccommodationPrice(accommodation, basePrice, travellersTotalCount);
		})
		.filter(_ => _)
		.sort((a, b) => a - b);

	// Quartiles et on regarde si c'est la même valeur alors on arrondi à 50 supérieur
	const q1 = round50(getPercentile(allPrices, 25));
	const median = round50(getPercentile(allPrices, 50));
	const q3 = round50(getPercentile(allPrices, 75));

	const isOneSameFilter = q1 === median || median === q3;

	return isOneSameFilter
		? []
		: [{ min: 0, max: q1 }, { min: q1, max: median }, { min: median, max: q3 }, { min: q3 }];
}

export function getAccommodationPrice(
	accommodation,
	basePrice,
	travellersTotalCount,
	flightUpgradePrice = 0,
	luggageUpgradePrice = 0
) {
	let total = basePrice;
	if (accommodation.accommodationItems) {
		// get room upgrade price
		const includedAccommodationItem = accommodation.accommodationItems[0];
		const upgradePrice = includedAccommodationItem.upgradePrice || 0;
		total = total + upgradePrice;

		// get board data
		// TODO delete .codes which is replaced by .boards
		const boards = includedAccommodationItem.boards || includedAccommodationItem.codes;
		let boardToDisplay = boards[0] || {};
		const boardUpgradePrice = boardToDisplay.upgradePrice || 0;
		total += boardUpgradePrice;

		total += flightUpgradePrice;

		total += luggageUpgradePrice;

		total = Math.ceil(total / travellersTotalCount);
	}

	return total;
}

export function getPercentile(data, percentile) {
	let index = (percentile / 100) * data.length;
	let result;
	if (Math.floor(index) === index) {
		result = (data[index - 1] + data[index]) / 2;
	} else {
		result = data[Math.floor(index)];
	}
	return result;
}

export function round50(x) {
	return Math.round(x / 50) * 50;
}

export const mapOcupanciesToPassengers = (occupancies = []) => {
	const passsengers = [];
	let adultIndex = 1,
		childrenIndex = 1;

	occupancies.forEach(occupancy => {
		if (occupancy?.adults > 0) {
			range(0, occupancy.adults).forEach(() => {
				passsengers.push({ type: PASSENGER_TYPE.ADULT, index: adultIndex });
				adultIndex += 1;
			});
		}
		if (occupancy?.children > 0) {
			range(0, occupancy?.children).forEach(index => {
				passsengers.push({
					type: PASSENGER_TYPE.CHILD,
					index: childrenIndex,
					birthdate: occupancy?.childrenBirthdates[index],
				});
				childrenIndex += 1;
			});
		}
	});

	return orderBy(passsengers, ["type"], ["asc"]);
};

export const buildSDPSearchPayload = data => {
	const occupancies = data.occupancies.map(occupancy => {
		const childrenBirthdates =
			occupancy.childrenBirthdates &&
			occupancy.childrenBirthdates.map(birthdates => {
				return birthdates;
			});

		return {
			...occupancy,
			childrenBirthdates,
		};
	});

	data.page = 1;
	data.pageSize = 48;

	// ex Paris : Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
	let departureDate = data.travelDates?.departureDate;
	// => Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
	departureDate = subHours(departureDate, departureDate.getTimezoneOffset() / 60);
	// departureDate.geTime() gives 11/06/2021
	// new Date(departureDate.geTime()) is stored in booking reducer abd used to initialize the form and gives Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)

	const queryData = {
		cabinCode: data.cabin.code,
		duration: data.duration?.code,
		departureDate: departureDate.getTime(),
		occupancies: occupancies,
		page: data.page,
		pageSize: data.pageSize,
	};

	// isHotel is set by toHotelSearchOption function in component DestinationCombobox
	if (data.destinationResort?.isHotel) {
		queryData.accommodationId = data?.destinationResort?.code;
		// even if a hotel is selected, we need to pass destinationResortCode in sdp listing param
		// so the name of the destination is displayed in the search form
		queryData.destinationResortCode = data?.destinationResort?.places?.[0]?.id;
	} else {
		queryData.destinationResortCode = data.destinationResort.code;
	}

	if (data.departureCity?.code) {
		queryData.departureCityCode = data.departureCity.code;
	}

	// TODO endDate may not be available
	let endDate = data.travelDates?.endDate;
	if (endDate) {
		endDate = subHours(endDate, endDate.getTimezoneOffset() / 60);
		queryData.endDate = endDate.getTime();
	}

	// STOPOVER
	if (data.stopoverCity?.code) {
		queryData.stopoverCityId = data.stopoverCity.code;
	}

	// STOPOVER
	if (data.stopoverDirection) {
		queryData.stopoverDirection = data.stopoverDirection.code;
	}

	const queryParams = stringify(queryData, {
		encodeValuesOnly: true,
		arrayFormat: "indices",
	});

	sendTagOnSDPSearch(queryData);

	return queryParams;
};

export const mapOccupanciesParamsToStore = (occupancies = []) => {
	return occupancies?.map(occupancy => {
		const childrenCount = Number(occupancy.children);

		if (childrenCount > 0) {
			// childrenBirthdates should not be empty to display form errors on empty childen birthdates fields to invite the user to fill them
			let childrenBirthdates = [...Array(childrenCount)].map(() => ({
				day: undefined,
				month: undefined,
				year: undefined,
			}));

			// childrenBirthdate.day="" from query params if coming from emirates website
			// because they do not provide birthdates
			if (occupancy?.childrenBirthdates) {
				childrenBirthdates = occupancy?.childrenBirthdates?.map(childrenBirthdate => {
					return {
						day:
							childrenBirthdate.day || childrenBirthdate.day === 0
								? Number(childrenBirthdate.day)
								: undefined,
						month:
							childrenBirthdate.month || childrenBirthdate.month === 0
								? Number(childrenBirthdate.month)
								: undefined,
						year:
							childrenBirthdate.year || childrenBirthdate.year === 0
								? Number(childrenBirthdate.year)
								: undefined,
					};
				});
			}
			return {
				adults: Number(occupancy.adults),
				children: Number(occupancy.children),
				childrenBirthdates,
			};
		}
		return { adults: Number(occupancy.adults), children: 0 };
	});
};

export const buildHotelOnlySearchParamsForDestination = destinationResortCode => {
	const queryData = {
		duration: HOTELONLY_SEARCH_DURATION,
		departureDate: addDays(new Date(), HOTELONLY_SEARCH_OFFSET).getTime(),
		endDate: addDays(new Date(), HOTELONLY_SEARCH_OFFSET + HOTELONLY_SEARCH_DURATION).getTime(),
		occupancies: [
			{
				adults: HOTELONLY_SEARCH_ADULT,
				children: HOTELONLY_SEARCH_CHILDREN,
			},
		],
		fromMerch: true,
		destinationResortCode,
	};

	const queryParams = stringify(queryData, {
		encodeValuesOnly: true,
		arrayFormat: "indices",
	});

	sendTagOnSDPSearch(queryData);

	return queryParams;
};

export const normalizeDate = date => {
	// @see SmartDPSearch in handleFormSubmission
	// ex Paris : Fri Jun 11 2021 02:00:00 GMT+0200 (heure d’été d’Europe centrale)
	const newDate = new Date(date);
	// => Fri Jun 11 2021 00:00:00 GMT+0200 (heure d’été d’Europe centrale)
	return addHours(newDate, newDate.getTimezoneOffset() / 60);
	// endDate.geTime() gives 10/6/2021 à 22:00:00
};

/*
 * This function calculates the valid duration based on the query parameters and availabilities date.
 * It checks if the duration is provided in the query parameters,
 * if not, it checks the availabilities date for the departure and end dates.
 * If the data is not available, it falls back to calculate duration from departureDate and end date
 * It returns the duration in days.
 */
export const findQuotePayloadDuration = ({ queryParams, availabilitiesDate = {} }) => {
	if (queryParams?.duration) {
		return Number(queryParams?.duration);
	}
	if (isEmpty(availabilitiesDate) || !(queryParams?.departureDate && queryParams?.endDate)) {
		return undefined;
	}

	const departureDate = new Date(Number(queryParams?.departureDate));
	const departureDateYMD = format(
		new Date(
			Date.UTC(
				departureDate.getUTCFullYear(),
				departureDate.getUTCMonth(),
				departureDate.getUTCDate()
			)
		),
		SDP_ROTATION_DATE_FORMAT
	);
	const endDate = new Date(Number(queryParams?.endDate));
	const endDateYMD = format(
		new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate())),
		SDP_ROTATION_DATE_FORMAT
	);
	const dataOfSelectedDepartureDate = availabilitiesDate[departureDateYMD]?.find(
		data => data.ed === endDateYMD
	);

	return (
		dataOfSelectedDepartureDate?.duration ||
		getDuration(queryParams?.departureDate, queryParams?.endDate)?.day
	);
};

export const transformAvailabilityDates = data => {
	return data?.reduce((acc, { prices, value }) => {
		// eslint-disable-next-line no-unused-expressions
		prices?.map(({ dd, ed }) => {
			const departureDate = format(new Date(dd), SDP_ROTATION_DATE_FORMAT);
			acc[departureDate] = acc[departureDate] || [];
			acc[departureDate].push({
				duration: value,
				ed: ed ? format(new Date(ed), SDP_ROTATION_DATE_FORMAT) : null,
			});
		});
		return acc;
	}, {});
};

export const buildSDPAvailabilityUri = ({
	departureCities = [],
	destinations = [],
	queryParams,
}) => {
	if (
		departureCities.length === 0 ||
		destinations.length === 0 ||
		!queryParams?.departureCityCode
	) {
		return undefined;
	}

	const { departure, destination } = getTravelLocationDataFromQuery({
		queryParams: {
			departureCityCode: queryParams?.departureCityCode,
			...(queryParams?.destinationResortCode && {
				destinationResortCode: queryParams?.destinationResortCode,
			}),
			...(queryParams?.destinationIATA && {
				destinationIATA: queryParams?.destinationIATA,
			}),
		},
		destinations: destinations,
		departures: departureCities,
	});
	if (isEmpty(destination) || isEmpty(departure)) {
		return undefined;
	}
	return `sdp${destination?.code.toString()}-${departure?.code.toString()}`;
};

export const mapQueryParamsToBooking = ({
	destinations = [],
	departureCities = [],
	cabins = [],
	queryParams,
}) => {
	const newOccupancies = mapOccupanciesParamsToStore(queryParams.occupancies);

	// convertir les occupancies en passagers pour la saisie des passagers sur la page de paiement
	const passengers = mapOcupanciesToPassengers(newOccupancies);

	const adults = passengers.filter(passenger => passenger.type === PASSENGER_TYPE.ADULT) || [];
	const children = passengers.filter(passenger => passenger.type === PASSENGER_TYPE.CHILD) || [];

	// sauvegarde des params de l'url dans le store pour l'affichage du search summary
	// nécessaire car on fait un clearBooking quand on click sur un produit flashsale

	const {
		departure: departureCityData,
		destination: destinationData,
	} = getTravelLocationDataFromQuery({
		queryParams,
		departures: departureCities,
		destinations,
	});

	const cabinData = cabins.find(cabin => cabin.code === queryParams.cabinCode);

	let bookingToSave = {
		departureCity: {
			code: departureCityData?.code,
			label: departureCityData?.label,
		},
		destinationResort: {
			code: String(destinationData?.code),
			label: destinationData?.label,
			type: destinationData?.type,
		},
		departureDate: queryParams.departureDate && Number(queryParams.departureDate),
		endDate: queryParams.endDate && Number(queryParams.endDate),
		cabin: {
			code: queryParams.cabinCode,
			label: cabinData?.label,
		},
		occupancies: newOccupancies,
		passengers,
		adults: adults.length || 2,
		children: children.length,
	};

	if (queryParams?.duration) {
		bookingToSave.duration = {
			value: Number(queryParams?.duration),
		};
	}

	if (queryParams.stopoverCityId) {
		bookingToSave.stopoverCityId = Number(queryParams.stopoverCityId);
	}

	if (queryParams.stopoverDirection) {
		bookingToSave.stopoverDirection = Number(queryParams.stopoverDirection);
	}

	return bookingToSave;
};

export const smartDPDestinationSuggestion = (input = "", levels = []) => {
	if (levels?.length === 0) {
		return undefined;
	}

	const country = levels.find(lvl => lvl.level === DESTINATION_LEVELS.COUNTRY)?.label;
	const resort = levels.find(lvl => lvl.level === DESTINATION_LEVELS.RESORT)?.label;
	const province = levels.find(lvl => lvl.level === DESTINATION_LEVELS.PROVINCE)?.label;
	const region = levels.find(lvl => lvl.level === DESTINATION_LEVELS.REGION)?.label;
	const zone = levels.find(lvl => lvl.level === DESTINATION_LEVELS.ZONE)?.label;
	const nearTo = levels.find(lvl => lvl.level === DESTINATION_LEVELS.NEARTO)?.label;

	if (country && input === country) {
		return undefined;
	} else if (country && [province, region, zone]?.filter(Boolean)?.includes(input)) {
		return country;
	} else if ([resort, nearTo]?.filter(Boolean)?.includes(input)) {
		return [...new Set([region, country].filter(Boolean))].join(", ");
	}
	return undefined;
};

/**
 * Retourne la première valeur valide selon l'ordre suivant :
 * 1. Valeur issue du booking lorsque la persistance est active.
 * 2. Valeur provenant d'une requête.
 * 3. Dernière recherche (si autorisée et valide).
 */
export const pickInitialValue = ({
	disableFormPersistance,
	bookingValue,
	queryValue,
	lastSearchValue,
	allowLastSearch = true,
	isValueValid = value => !isNil(value),
}) => {
	if (!disableFormPersistance && isValueValid(bookingValue)) {
		return bookingValue;
	}

	if (isValueValid(queryValue)) {
		return queryValue;
	}

	if (!disableFormPersistance && allowLastSearch && isValueValid(lastSearchValue)) {
		return lastSearchValue;
	}

	return undefined;
};

// Après un clearBooking on retombe sur 2 adultes; on ne considère la persistance
// que si l'utilisateur s'est écarté de cette configuration par défaut.
export const hasCustomBookingOccupancies = booking => {
	const occupancies = booking?.occupancies || [];
	if (occupancies.length === 0) {
		return false;
	}
	if (occupancies.length > 1) {
		return true;
	}
	const [firstOccupancy] = occupancies;
	return !(
		firstOccupancy?.adults === 2 &&
		(firstOccupancy?.children || 0) === 0 &&
		(firstOccupancy?.childrenBirthdates?.length || 0) === 0
	);
};
